import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BurgerOrder, ORDER_STATUS_MN } from '../types';
import { mockDataService } from '../services/mockData';
import './AdminPanel.css';

const AdminPanel: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();
  const [orders, setOrders] = useState<BurgerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  useEffect(() => {
    if (!currentUser || !isAdmin) return;

    const fetchAllOrders = async () => {
      try {
        try {
          // Эхлээд Firebase ашиглах оролдлого
          const q = query(
            collection(db, 'orders'),
            orderBy('orderDate', 'desc')
          );
          
          const querySnapshot = await getDocs(q);
          const ordersData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            orderDate: doc.data().orderDate.toDate()
          })) as BurgerOrder[];
          
          setOrders(ordersData);
        } catch (firebaseError) {
          // Firebase алдаа гарвал mock service ашиглах
          console.log('Firebase ашиглах боломжгүй, demo режимд ажиллаж байна');
          mockDataService.initializeDemoData();
          const mockOrders = await mockDataService.getAllOrders();
          const processedOrders = mockOrders.map(order => ({
            ...order,
            orderDate: new Date(order.orderDate)
          }));
          setOrders(processedOrders);
        }
      } catch (error) {
        console.error('Захиалгууд татахад алдаа гарлаа:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllOrders();
  }, [currentUser, isAdmin]);

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      try {
        // Эхлээд Firebase ашиглах оролдлого
        const orderRef = doc(db, 'orders', orderId);
        await updateDoc(orderRef, {
          status: newStatus
        });
      } catch (firebaseError) {
        // Firebase алдаа гарвал mock service ашиглах
        await mockDataService.updateOrderStatus(orderId, newStatus);
      }

      // Update local state
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId ? { ...order, status: newStatus as any } : order
        )
      );

      alert('Захиалгын статус амжилттай шинэчлэгдлээ!');
    } catch (error) {
      console.error('Статус шинэчлэхэд алдаа гарлаа:', error);
      alert('Статус шинэчлэхэд алдаа гарлаа.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'confirmed':
      case 'preparing':
        return 'status-confirmed';
      case 'ready':
      case 'delivered':
        return 'status-delivered';
      default:
        return '';
    }
  };

  const getFilteredOrders = () => {
    if (selectedStatus === 'all') {
      return orders;
    }
    return orders.filter(order => order.status === selectedStatus);
  };

  const getOrderStats = () => {
    const stats = {
      total: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      confirmed: orders.filter(o => o.status === 'confirmed').length,
      preparing: orders.filter(o => o.status === 'preparing').length,
      ready: orders.filter(o => o.status === 'ready').length,
      delivered: orders.filter(o => o.status === 'delivered').length,
      totalRevenue: orders.reduce((sum, order) => sum + order.totalPrice, 0)
    };
    return stats;
  };

  if (!currentUser || !isAdmin) {
    return (
      <div className="admin-panel">
        <div className="access-denied">
          <h2>Хандах эрх байхгүй</h2>
          <p>Энэ хуудас зөвхөн админд зориулагдсан.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading">
          <div className="spinner"></div>
          <p>Захиалгууд ачааллаж байна...</p>
        </div>
      </div>
    );
  }

  const stats = getOrderStats();
  const filteredOrders = getFilteredOrders();

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>🔧 Админ удирдлага</h1>
        <p>Захиалгуудыг удирдах хяналтын самбар</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Нийт захиалга</h3>
          <div className="stat-number">{stats.total}</div>
        </div>
        <div className="stat-card">
          <h3>Хүлээгдэж буй</h3>
          <div className="stat-number pending">{stats.pending}</div>
        </div>
        <div className="stat-card">
          <h3>Баталгаажсан</h3>
          <div className="stat-number confirmed">{stats.confirmed}</div>
        </div>
        <div className="stat-card">
          <h3>Хүргэгдсэн</h3>
          <div className="stat-number delivered">{stats.delivered}</div>
        </div>
        <div className="stat-card revenue">
          <h3>Нийт орлого</h3>
          <div className="stat-number">{stats.totalRevenue.toLocaleString()}₮</div>
        </div>
      </div>

      <div className="controls">
        <div className="filter-controls">
          <label>Статусаар шүүх:</label>
          <select 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="form-control"
          >
            <option value="all">Бүгд</option>
            <option value="pending">Хүлээгдэж буй</option>
            <option value="confirmed">Баталгаажсан</option>
            <option value="preparing">Бэлтгэж буй</option>
            <option value="ready">Бэлэн болсон</option>
            <option value="delivered">Хүргэгдсэн</option>
          </select>
        </div>
      </div>

      <div className="orders-list">
        {filteredOrders.length === 0 ? (
          <div className="no-orders">
            <p>Захиалга олдсонгүй.</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="admin-order-card">
              <div className="order-header">
                <div className="order-info">
                  <h3>Захиалга #{order.id?.slice(-6)}</h3>
                  <p className="order-date">
                    {order.orderDate.toLocaleDateString('mn-MN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div className={`order-status ${getStatusColor(order.status)}`}>
                  {ORDER_STATUS_MN[order.status]}
                </div>
              </div>

              <div className="order-content">
                <div className="order-details">
                  <div className="customer-info">
                    <h4>Хэрэглэгч:</h4>
                    <p><strong>Нэр:</strong> {order.customerName}</p>
                    <p><strong>Имэйл:</strong> {order.customerEmail}</p>
                    <p><strong>Утас:</strong> {order.customerPhone}</p>
                    <p><strong>Хаяг:</strong> {order.deliveryAddress}</p>
                  </div>

                  <div className="order-items">
                    <h4>Орцууд:</h4>
                    <ul>
                      {order.ingredients.map((ingredient, index) => (
                        <li key={index}>
                          {ingredient.nameMn} - {ingredient.price.toLocaleString()}₮
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="order-actions">
                  <div className="total-price">
                    <strong>Нийт: {order.totalPrice.toLocaleString()}₮</strong>
                  </div>
                  
                  <div className="status-controls">
                    <h4>Статус өөрчлөх:</h4>
                    <div className="status-buttons">
                      {order.status === 'pending' && (
                        <button
                          onClick={() => updateOrderStatus(order.id!, 'confirmed')}
                          className="btn btn-success btn-sm"
                        >
                          Баталгаажуулах
                        </button>
                      )}
                      {order.status === 'confirmed' && (
                        <button
                          onClick={() => updateOrderStatus(order.id!, 'preparing')}
                          className="btn btn-primary btn-sm"
                        >
                          Бэлтгэж эхлэх
                        </button>
                      )}
                      {order.status === 'preparing' && (
                        <button
                          onClick={() => updateOrderStatus(order.id!, 'ready')}
                          className="btn btn-primary btn-sm"
                        >
                          Бэлэн болгох
                        </button>
                      )}
                      {order.status === 'ready' && (
                        <button
                          onClick={() => updateOrderStatus(order.id!, 'delivered')}
                          className="btn btn-success btn-sm"
                        >
                          Хүргэлтэнд гаргах
                        </button>
                      )}
                      {order.status === 'delivered' && (
                        <span className="delivered-badge">✅ Хүргэгдсэн</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
